import type { ComputedRef } from 'vue'
import type { AppSelectOption } from '@brighterly/lib-core-types/ui/selects/interfaces'
import type {
  Subscription,
  SubscriptionAutoRenewTone,
  SubscriptionChargeNowPaymentMethod,
  SubscriptionCustomer,
  SubscriptionInterval,
} from '@entities/subscription'
import type { PluralForms } from '@shared/lib/format'

import { DateFormat } from '@brighterly/lib-core-types/date/enums'

import { useListsStore } from '@shared/model'
import { formatPrice, pluralize, useDayjs, withCount } from '@shared/lib/format'

import { SUBSCRIPTION_EMPTY_CELL } from './subscription-rules'

// codestyle-ignore: date-format-enum — month-day label for the next invoice; DateFormat has no month-day member (decided 2026-10-24)
const NEXT_INVOICE_DATE_FORMAT = 'MMM DD'

const CARD_NUMBER_MASK = '••••'

const INTERVAL_FORMS: Record<SubscriptionInterval, PluralForms> = {
  day: { one: 'day', other: 'days' },
  week: { one: 'week', other: 'weeks' },
  month: { one: 'month', other: 'months' },
  year: { one: 'year', other: 'years' },
}

const dayjs = useDayjs()

interface SubscriptionOptionsReturn {
  statusOptions: ComputedRef<AppSelectOption[]>
  getStatusLabel: (status: string) => string
  getProviderLabel: (provider: string) => string
  getDisciplineLabel: (code: string) => string
  getPaymentMethodLabel: (type: string) => string
}

const toOptions = (record: Record<string, string>): AppSelectOption[] =>
  Object.entries(record).map(([value, text]) => ({ value, text }))

/** `APPLE_PAY` → `Apple pay`, for a payment method type the lists do not name yet. */
export const humanizeEnumValue = (value: string): string => {
  const words = value.toLowerCase().replace(/_/g, ' ')

  return words.charAt(0).toUpperCase() + words.slice(1)
}

export const useSubscriptionOptions = (): SubscriptionOptionsReturn => {
  const listsStore = useListsStore()

  const statusMap = computed<Record<string, string>>(() => listsStore.getLists?.subscription?.status ?? {})
  const providerMap = computed<Record<string, string>>(() => listsStore.getLists?.subscription?.provider ?? {})
  const disciplineMap = computed<Record<string, string>>(() => listsStore.getLists?.discipline?.code ?? {})
  const paymentMethodMap = computed<Record<string, string>>(() => listsStore.getLists?.payment_method?.type ?? {})

  const statusOptions = computed<AppSelectOption[]>(() => toOptions(statusMap.value))

  const getStatusLabel = (status: string): string => statusMap.value[status] ?? status
  const getProviderLabel = (provider: string): string => providerMap.value[provider] ?? provider
  const getDisciplineLabel = (code: string): string => disciplineMap.value[code] ?? code
  const getPaymentMethodLabel = (type: string): string => paymentMethodMap.value[type] ?? humanizeEnumValue(type)

  return { statusOptions, getStatusLabel, getProviderLabel, getDisciplineLabel, getPaymentMethodLabel }
}

/** `Oct 20 for $359.10` — the next charge date and the amount it will take; a `$0.00` charge still shows. */
export const formatNextInvoice = (subscription: Subscription): string =>
  subscription.next_payment_at
    ? `${dayjs(subscription.next_payment_at).format(NEXT_INVOICE_DATE_FORMAT)} for ${formatPrice(subscription.pricing.first_payment)}`
    : SUBSCRIPTION_EMPTY_CELL

export const formatSubscriptionDate = (value: string): string => dayjs(value).format(DateFormat.FileDate)

export const formatSubscriptionDateTime = (value: string | null): string =>
  value ? dayjs(value).format(DateFormat.KanbanDate) : SUBSCRIPTION_EMPTY_CELL

export const getAutoRenewLabel = (enabled: boolean): string => (enabled ? 'Enabled' : 'Disabled')

/** Enabled reads as emerald and disabled as red — for the state and for the action that leads to it. */
export const getAutoRenewTone = (enabled: boolean): SubscriptionAutoRenewTone => (enabled ? 'emerald' : 'red')

/** `Ann / #77` — the customer as the subscription modals name them. */
export const formatSubscriptionCustomer = (customer: SubscriptionCustomer | null): string =>
  (customer ? `${customer.first_name} / #${customer.id}` : SUBSCRIPTION_EMPTY_CELL)

/** The hint under "Charge now": the payment it brings forward, or why it is off. */
export const getChargeNowHint = (subscription: Subscription): string | undefined => {
  const chargeNow = subscription.charge_now

  if (!chargeNow) return undefined
  if (!chargeNow.is_available) return chargeNow.unavailable_reason_label ?? undefined

  return `${formatPrice(subscription.pricing.first_payment)} · next payment ${formatSubscriptionDate(chargeNow.next_payment_at)}`
}

/** Stripe minor units in the charge's own currency: `14900`, `usd` → `$149.00`. */
export const formatChargeAmount = (amount: number, currency: string): string => {
  const formatter = new Intl.NumberFormat('en-US', { style: 'currency', currency: currency.toUpperCase() })
  const { maximumFractionDigits = 0 } = formatter.resolvedOptions()

  return formatter.format(amount / 10 ** maximumFractionDigits)
}

/** `Visa •••• 4242`, or the brand alone when Stripe has no last four digits. */
export const formatChargeCard = (paymentMethod: SubscriptionChargeNowPaymentMethod | null): string => {
  if (!paymentMethod) return SUBSCRIPTION_EMPTY_CELL

  const brand = humanizeEnumValue(paymentMethod.brand)

  return paymentMethod.last4 ? `${brand} ${CARD_NUMBER_MASK} ${paymentMethod.last4}` : brand
}

/** `every month`, `every 3 months`. */
export const formatBillingInterval = (interval: SubscriptionInterval, count: number): string => {
  const forms = INTERVAL_FORMS[interval]
  const isSingleInterval = count === 1

  return `every ${isSingleInterval ? pluralize(count, forms) : withCount(count, forms)}`
}
